import type { ReactNode } from "react";
import { ProgressBar, Skeleton } from "@heroui/react";
import { getQuotaProgressColor, getQuotaUsageText } from "./quota";

export type StorageQuotaBarProps = {
  used: number;
  count: number;
  isPending?: boolean;
  /** 进度条粗细：sm 对应原 web 列表页，lg 用于「我的」页 */
  size?: "sm" | "md" | "lg";
  /** 右侧操作区，例如「管理」链接或「购买容量包」按钮 */
  action?: ReactNode;
  className?: string;
  label?: string;
};

export default function StorageQuotaBar({
  used,
  count,
  isPending = false,
  size = "sm",
  action,
  className,
  label = "已使用",
}: StorageQuotaBarProps) {
  const color = getQuotaProgressColor(used, count);
  const usageText = getQuotaUsageText(used, count);
  const trackClass =
    size === "lg" ? "h-3" : size === "md" ? "h-2.5" : undefined;

  return (
    <div className={className}>
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="text-sm text-muted">{label}</span>
        {isPending ? (
          <Skeleton className="h-4 w-24 rounded" />
        ) : (
          <span className="text-sm font-medium text-foreground tabular-nums">{usageText}</span>
        )}
        {action}
      </div>
      {isPending ? (
        <Skeleton className={`w-full rounded-full ${size === "lg" ? "h-3" : "h-2"}`} />
      ) : (
        <ProgressBar
          aria-label={`存储配额 ${usageText}`}
          color={color}
          maxValue={count || 1}
          minValue={0}
          size={size === "lg" ? "md" : "sm"}
          value={Math.min(used, count || 1)}
        >
          <ProgressBar.Track className={trackClass}>
            <ProgressBar.Fill />
          </ProgressBar.Track>
        </ProgressBar>
      )}
    </div>
  );
}
